/**
 * @file block-errors.ts
 * @description Ошибки сервера при правке блоков страницы → слова владельца
 * (phase19.16 Р2; контракт §16.2–§16.4; uiux-design-r2 §10).
 *
 * Реестр по коду ошибки (OCP): новый код — новая строка. Неизвестный код —
 * текст сервера (он уже по-русски) или запасная фраза.
 */

import { SITE_LIMITS } from "@chaiberi/shared";

import { ApiError } from "../api-client";

/**
 * 409 `section_format_outdated` (SB120 этап 3а, §7): формат блоков на
 * сервере новее этой вкладки кабинета — запись не прошла, нужна свежая
 * страница.
 */
export const SECTION_FORMAT_OUTDATED_TEXT = "Конструктор обновился — обновите страницу кабинета (F5), затем повторите правку. Несохранённые правки этого блока пропадут.";

/** 409 `block_changed` (§16.2): правки остаются в форме, страница перечитывается. */
export const BLOCK_CHANGED_TEXT = "Блок изменили в другой вкладке или другой сотрудник. Мы загрузили свежую версию страницы, ваши правки остались в форме — проверьте и сохраните ещё раз.";

export const BLOCK_ERROR_TEXT: Readonly<Record<string, string>> = {
  block_changed: BLOCK_CHANGED_TEXT,
  page_blocks_limit: `На странице ${SITE_LIMITS.sectionsPerPage} блоков — больше не поместится. Удалите лишний или перенесите часть на другую страницу.`,
  preset_unavailable: "Блок подписки появится, когда у магазина заработает рассылка.",
  section_newsletter_unavailable: "Подписку можно добавить, когда у магазина заработает рассылка.",
  preset_not_found: "Такой заготовки больше нет — обновите страницу кабинета.",
  block_type_retired: "Прежние блоки больше не добавляются — возьмите заготовку или пустой блок.",
  media_foreign: "Файла нет в медиатеке магазина — выберите картинку заново.",
  site_block_anchor_invalid: "Блок, после которого вставляли, уже убрали — новый блок встал в конец страницы.",
  page_hidden: "Сначала покажите страницу на сайте.",
  site_page_already_home: "Эта страница уже главная.",
  site_page_slot_forbidden: "Блоки над лентой и под статьёй главной быть не могут.",
  site_page_previous_slug_required: "Укажите новый адрес прежней главной.",
  site_page_slug_reserved: "Этот адрес занят разделом сайта — выберите другой.",
  site_page_slug_taken: "Такой адрес уже есть у другой страницы.",
  page_referenced: "На страницу ведёт ссылка «Условия доставки и оплаты» в подвале сайта. Сначала выберите другую страницу в «Настройки сайта → Бренд».",
  // Р2а (контракт §19): шаблон страницы товара.
  product_template_required: "Обязательный элемент шаблона (название, цена и покупка, сведения о товаре, продавец) нельзя удалить или скрыть — его можно только переставить.",
  site_template_unknown: "Такого шаблона нет — обновите страницу кабинета.",
  site_page_template_forbidden: "Шаблон страницы товара публикуется вместе с видом сайта — кнопкой «Опубликовать».",
  preset_layout_use_reset: "Раскладка страницы товара заменяет весь шаблон — выберите её в «Добавить блок → Страница товара».",
  product_template_layout_unknown: "Такой раскладки больше нет — обновите страницу кабинета.",
  site_element_not_found: "Элемента уже нет в блоке — страница обновлена.",
  site_column_invalid: "Такой колонки в блоке нет — выберите другую.",
  // SB120 этап 3а (db-design-sb120-stage3 §6, §7).
  section_format_outdated: SECTION_FORMAT_OUTDATED_TEXT,
  element_font_not_found: "Такого шрифта нет в медиатеке магазина — выберите другой шрифт.",
};

/** Нет связи с сервером (fetch не дошёл) — правки остаются в форме. */
export const OFFLINE_TEXT = "Не сохранилось — нет связи с сервером. Правки остались в форме.";

/** Текст ошибки действия с блоком. */
export function blockErrorText(err: unknown, fallback: string): string {
  if (err instanceof ApiError) {
    // Сбой сервера — его текст не для владельца.
    if (err.status >= 500) return `${fallback} Сервер не ответил — попробуйте ещё раз.`;
    // Лимит блоков у шаблона товара свой (20) — число из ответа сервера.
    const max = (err.details as { max?: unknown } | null | undefined)?.max;
    if (err.code === "page_blocks_limit" && typeof max === "number" && max !== SITE_LIMITS.sectionsPerPage) {
      return `Блоков уже ${max} — больше не поместится. Удалите лишний блок.`;
    }
    return BLOCK_ERROR_TEXT[err.code] ?? (err.message || fallback);
  }
  if (err instanceof TypeError) return OFFLINE_TEXT;
  return fallback;
}

/**
 * Коды, у которых в `details.issues` замечания по полям секции (Р2а — и
 * проверка шаблона товара; SB120 этап 3а — неизвестный свой шрифт
 * `element_font_not_found`, `{ path, key, message }`).
 */
const ISSUE_CODES: ReadonlySet<string> = new Set(["section_payload_invalid", "product_template_invalid", "product_template_required", "element_font_not_found"]);

/** Замечания проверки секции из ответа 422 `section_payload_invalid` / `product_template_invalid`. */
export function payloadIssues(err: unknown): Array<{ path: string; message: string }> {
  if (!(err instanceof ApiError) || !ISSUE_CODES.has(err.code)) return [];
  const details = err.details as { issues?: unknown } | null | undefined;
  if (!details || !Array.isArray(details.issues)) return [];
  // Замечание шаблона товара может прийти без пути (о шаблоне целиком) — тогда путь пустой.
  return details.issues.flatMap((i: unknown) => {
    if (typeof i !== "object" || i === null) return [];
    const { path, message } = i as { path?: unknown; message?: unknown };
    return typeof message === "string" ? [{ path: typeof path === "string" ? path : "", message }] : [];
  });
}
